import './index.less'
import * as d3 from 'd3'
import China from '@/assets/data/China.json'
import { useEffect, useState } from 'react'
import { getAuthorDistribution } from '@/server'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { setToken } from '@/features/Login/login'
import { useNavigate } from 'react-router-dom'

export function AuthorDistribution() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const token = useAppSelector((state) => state.login.token)
  //作者作品数量排名
  const [authorWorkTop, setAuthorWorkTop] = useState<{ name: string; workNum: number }[]>([])
  //省份作品数量排名
  const [provinceWorkTop, setProvinceWorkTop] = useState([])
  //作品分布地图
  const drawPaintingMap = function (provinceInfo) {
    d3.select('#author-map').selectChildren('*')?.remove()
    // The svg
    const width = document.getElementById('author-map')?.clientWidth ?? 0,
      height = document.getElementById('author-map')?.clientHeight ?? 0
    const svg = d3
      .select('#author-map')
      ?.append('svg')
      .attr('width', width)
      .attr('height', height)
      .call(
        d3
          .zoom()
          .scaleExtent([0.8, 5])
          .on('zoom', function (event) {
            svg.attr('transform', event.transform)
          })
      )
      .append('g')

    // Map and projection
    const projection = d3
      .geoMercator()
      .center([105, 31.8]) // GPS of location to zoom on
      .scale(263) // This is like the zoom
      .translate([width / 2, height / 2])

    //地图数据
    const dataGeo = China
    //省份对应坐标
    const _provinceCenterPos = dataGeo.features.map((d) => {
      return { name: d.properties.name, center: d.properties.centroid }
    })
    const provinceCenterPos = {}
    for (let i = 0; i < _provinceCenterPos.length; i++)
      provinceCenterPos[_provinceCenterPos[i].name] = _provinceCenterPos[i].center

    //展示数据
    const data: {
      name: string
      lat: number
      long: number
      num: number
      topValues: { name: string; workNum: number }[]
    }[] = []
    for (let i = 0; i < provinceInfo.length; i++) {
      const nameKey = provinceInfo[i].name
      data.push({
        name: nameKey,
        long: provinceCenterPos[nameKey][0],
        lat: provinceCenterPos[nameKey][1],
        num: provinceInfo[i].workNum,
        topValues: provinceInfo[i].topValues
      })
    }
    //先渲染半径大的circle
    data.sort((a, b) => b.num - a.num)
    const dataNum = data.map((d) => d.num)
    // Create a color scale
    const colorLinear = d3
      .scaleLinear()
      .domain([d3.min(dataNum), d3.max(dataNum)])
      .range([0, 1])
    const compute = d3.interpolate('#dbd5bc', '#957966')

    // Add a scale for bubble size
    const valueExtent = d3.extent(data, (d) => d.num)
    const size = d3
      .scaleSqrt()
      .domain(valueExtent) // What's in the data
      .range([2, 10]) // Size in pixel

    // Draw the map
    svg
      .append('g')
      .selectAll('path')
      .data(dataGeo.features)
      .join('path')
      .attr('fill', 'none')
      .attr('d', d3.geoPath().projection(projection))
      .style('stroke', '#e4e4e4')

    // create a tooltip
    const Tooltip = d3
      .select('#author-map')
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 1)
      .style('background-color', 'white')
      .style('border', 'solid')
      .style('border-width', '2px')
      .style('border-radius', '5px')
      .style('padding', '5px')
    const mouseover = function (_event, _d) {
      Tooltip.style('display', 'block')
    }
    const mousemove = function (event, d) {
      const rect = document.getElementById('author-map')?.getBoundingClientRect()
      Tooltip.html(
        `<div class='tooltip-item'><div class='author-name'>${
          d.name
        }</div><div class='author-num'>${d.num}</div></div>${d.topValues
          .map((item, index) => {
            if (item.workNum > 0)
              return (
                "<div class='tooltip-item'> <div class='author-name'>" +
                (index + 1) +
                '.' +
                item.name +
                "&nbsp;</div><div class='author-num'>" +
                item.workNum +
                '</div></div>'
              )
          })
          .join('')}`
      )
        .style('left', event.x - (rect?.left ?? 0) + 15 + 'px')
        .style('top', event.y - (rect?.top ?? 0) + 15 + 'px')
    }
    const mouseleave = function (_event, _d) {
      Tooltip.style('display', 'none')
    }

    // Add circles:
    svg
      .selectAll('myCircles')
      .data(data)
      .join('circle')
      .attr('cx', (d) => projection([d.long, d.lat])[0])
      .attr('cy', (d) => projection([d.long, d.lat])[1])
      .attr('r', (d) => size(d.num))
      .attr('fill', (d) => compute(colorLinear(d.num)))
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseleave', mouseleave)

    // Add texts:
    const fontSize = 4
    svg
      .selectAll('myTexts')
      .data(data)
      .join('text')
      .attr('x', (d) => projection([d.long, d.lat])[0] - (fontSize * d.name.length) / 2)
      .attr('y', (d) => projection([d.long, d.lat])[1] + fontSize / 2)
      .attr('fill', '#ffffff')
      .style('font-size', fontSize)
      .style('font-weight', 400)
      .style('pointer-events', 'none')
      .style('user-selected', 'none')
      .text((d) => d.name)
      .attr('display', (d) => {
        return size(d.num) > 4 ? 'block' : 'none'
      })
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseleave', mouseleave)
  }
  //初始化作品分布地图
  useEffect(() => {
    getAuthorDistribution(token).then((res) => {
      if (res.data.code === '10056') {
        //token失效
        window.confirm('登录已过期，请重新登录！')
        dispatch(setToken(''))
        navigate('/')
      } else {
        const authorInfo = res.data.content.author
        const provinceInfo = res.data.content.province
        setAuthorWorkTop(authorInfo)
        setProvinceWorkTop(provinceInfo)
        drawPaintingMap(provinceInfo)
      }
    })
  }, [])
  //浏览器窗口大小改变时，重新绘制svg
  useEffect(() => {
    const func = function () {
      drawPaintingMap(provinceWorkTop)
    }
    window.addEventListener('resize', func)
    return () => {
      window.removeEventListener('resize', func)
    }
  }, [provinceWorkTop])
  return (
    <div className='author-distribution-content'>
      <div className='author-distribution-map'>
        <div id='author-map'></div>
      </div>
      <div className='author-distribution-list'>
        <div className='author-distribution-list-title'>作者作品数排名</div>
        <div className='author-distribution-list-content'>
          {authorWorkTop.map((item, index) => {
            return (
              <div className='author-distribution-list-item' key={index}>
                <div className={'serial-num' + (index < 3 ? ' serial-num-top' : '')}>
                  {index + 1}
                </div>
                <div className='museum-name'>{item.name}</div>
                &nbsp;&nbsp;
                <div className='museum-num'>
                  {String(item.workNum).replace(/(\d)(?=(\d{3})+$)/g, '$1,')}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
